import React from 'react';
import styled from '@emotion/styled';
import { Box, Text, Flex, Button } from '@updater/ui-uds';
import { ChatColors } from '../../../configs/chat-colors';
import DotsThreeOutlineVertical from '../../../assets/DotsThreeOutlineVertical.svg';

const HeaderContainer = styled(Flex)`
  border-bottom: ${ChatColors.header.BORDER};
  background: ${ChatColors.header.BG};
  backdrop-filter: blur(10px);
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 30;
  text-align: left;

  .header-new-conversation-label {
    display: none;
  }
  @media (min-width: 480px) {
    .header-new-conversation-label {
      display: inline;
    }
  }
`;
const ActionButton = styled(Button)`
  min-width: 24px;
  min-height: 24px;
  color: ${ChatColors.header.TEXT};
  border: 0;
  background: transparent;
  padding: 0.375rem 0.75rem;
  &:hover {
    background: transparent;
    > svg {
      > path {
        fill: ${ChatColors.header.ACTION_ICON};
      }
    }
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const IconContainer = styled(Box)`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-image: url('./images/nav-icons/updater_logo_icon.png');
  background-size: contain;
  background-color: ${ChatColors.header.ICON_BG};
  opacity: ${ChatColors.header.ICON_OPACITY};
`;

type HeaderProps = React.PropsWithChildren<{
  title?: string;
  onOpenSidebar?: () => void;
  onBack?: () => void;
}>;

const Header: React.FC<HeaderProps> = ({
  title,
  onOpenSidebar,
  onBack,
  children,
}: HeaderProps) => {
  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else if (typeof window !== 'undefined' && window.history) {
      window.history.back();
    }
  };

  return (
    <HeaderContainer role="banner" flexDirection="column">
      <Flex
        width="100%"
        height="68px"
        justifyContent="space-between"
        style={{ padding: '1rem 0 1rem 1.25rem' }}
      >
        <Flex gap="s">
          <IconContainer
            backgroundColor="white"
            aria-hidden="true"
            onClick={handleBackClick}
            data-testid="back-button"
            data-cy="back-button"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBackClick();
              }
            }}
            aria-label="Go back"
          ></IconContainer>
          <Flex flexDirection="column">
            <Box style={{ minWidth: 0 }}>
              <Text
                variant="mBold"
                color={ChatColors.header.TEXT}
                as="h1"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.2',
                  maxHeight: '2.4em',
                }}
              >
                {title ?? 'Virtual Building Assistant'}
              </Text>
            </Box>
            <Box style={{ minWidth: 0 }}>
              <Text
                variant="s"
                color={ChatColors.header.TEXT}
                marginTop="xxs"
                as="h2"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.2',
                  maxHeight: '2.4em',
                }}
              >
                {title ? 'Move-in Assistant' : ''}
              </Text>
            </Box>
          </Flex>
        </Flex>
        <ActionButton
          onClick={onOpenSidebar}
          aria-label="Open menu"
          data-testid="open-vba-sidebar"
        >
          <DotsThreeOutlineVertical />
        </ActionButton>
      </Flex>
      <Box width="100%">{children}</Box>
    </HeaderContainer>
  );
};

export default Header;
